import { useEffect, useState, useCallback } from "react"
import { useTranslations } from "next-intl"
import {
	Send, Plus, Mail, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
	CheckCircle2, XCircle, Clock, Eye, Trash2, CheckCheck, Loader2,
	Building2, Calendar, Banknote, ArrowLeft,
} from "lucide-react"
import { DashboardLayout } from "@/components/common/DashboardLayout"
import { LoadingSpinner } from "@/components/common/LoadingSpinner"
import { SourceBadge } from "@/components/common/SourceBadge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { api } from "@/lib/apiService"
import { formatCurrency, formatDate } from "@/lib/utils"
import type {
	OutreachCampaign,
	OutreachCandidate,
	OutreachEmail,
	OutreachProduct,
	CampaignListResponse,
	CampaignEmailsResponse,
	PreviewResponse,
	OutreachEmailStatus,
	OutreachRecipientType,
} from "@/types/outreach.types"

function Pagination({ page, pages, total, limit, onPageChange }: {
	page: number; pages: number; total: number; limit: number; onPageChange: (page: number) => void
}) {
	if (pages <= 1) return null
	const from = (page - 1) * limit + 1
	const to = Math.min(page * limit, total)

	return (
		<div className="flex items-center justify-between gap-4 flex-wrap">
			<p className="text-sm text-muted-foreground">{from}-{to} / {total}</p>
			<div className="flex items-center gap-1">
				<Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPageChange(1)}>
					<ChevronsLeft className="h-4 w-4" />
				</Button>
				<Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
					<ChevronLeft className="h-4 w-4" />
				</Button>
				<span className="text-sm px-3">{page} / {pages}</span>
				<Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => onPageChange(page + 1)}>
					<ChevronRight className="h-4 w-4" />
				</Button>
				<Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => onPageChange(pages)}>
					<ChevronsRight className="h-4 w-4" />
				</Button>
			</div>
		</div>
	)
}

const STATUS_CONFIG: Record<OutreachEmailStatus, { icon: typeof Clock; color: string; bg: string }> = {
	pending: { icon: Clock, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
	approved: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
	rejected: { icon: XCircle, color: "text-red-500", bg: "bg-red-50 border-red-200" },
	sent: { icon: Send, color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
	failed: { icon: XCircle, color: "text-red-600", bg: "bg-red-50 border-red-200" },
}

function RecipientBadge({ type }: { type: OutreachRecipientType }) {
	const t = useTranslations("outreach")
	if (type === "organization") {
		return (
			<Badge variant="outline" className="text-[9px] px-1.5 py-0 text-blue-600 border-blue-200 bg-blue-50 gap-1">
				<Building2 className="h-2.5 w-2.5" />
				{t("recipientOrg")}
			</Badge>
		)
	}
	return (
		<Badge variant="outline" className="text-[9px] px-1.5 py-0 text-emerald-600 border-emerald-200 bg-emerald-50 gap-1">
			<Mail className="h-2.5 w-2.5" />
			{t("recipientContact")}
		</Badge>
	)
}

const CAMPAIGN_STATUS_CONFIG: Record<string, { color: string; bg: string }> = {
	draft: { color: "text-gray-600", bg: "bg-gray-50 border-gray-200" },
	ready: { color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
	sending: { color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
	sent: { color: "text-blue-700", bg: "bg-blue-50 border-blue-200" },
	failed: { color: "text-red-600", bg: "bg-red-50 border-red-200" },
}

export default function OutreachPage() {
	const t = useTranslations("outreach")
	const [view, setView] = useState<"list" | "create" | "detail">("list")
	const [selectedCampaign, setSelectedCampaign] = useState<OutreachCampaign | null>(null)

	const openDetail = (campaign: OutreachCampaign) => {
		setSelectedCampaign(campaign)
		setView("detail")
	}

	return (
		<DashboardLayout>
			<div className="max-w-4xl mx-auto w-full">
				{view === "list" && (
					<CampaignList
						onCreateNew={() => setView("create")}
						onOpenCampaign={openDetail}
					/>
				)}
				{view === "create" && (
					<CreateCampaign
						onBack={() => setView("list")}
						onCreated={(campaign) => {
							setSelectedCampaign(campaign)
							setView("detail")
						}}
					/>
				)}
				{view === "detail" && selectedCampaign && (
					<CampaignDetail
						campaign={selectedCampaign}
						onBack={() => { setSelectedCampaign(null); setView("list") }}
						onUpdate={setSelectedCampaign}
					/>
				)}
			</div>
		</DashboardLayout>
	)
}

// ── Campaign List ──

function CampaignList({
	onCreateNew,
	onOpenCampaign,
}: {
	onCreateNew: () => void
	onOpenCampaign: (c: OutreachCampaign) => void
}) {
	const t = useTranslations("outreach")
	const [campaigns, setCampaigns] = useState<OutreachCampaign[]>([])
	const [isLoading, setIsLoading] = useState(true)
	const [total, setTotal] = useState(0)
	const [page, setPage] = useState(1)
	const [pages, setPages] = useState(1)
	const limit = 20

	const fetchCampaigns = useCallback(async () => {
		setIsLoading(true)
		try {
			const res = await api.get<CampaignListResponse>(`/outreach/campaigns?page=${page}&limit=${limit}`)
			setCampaigns(res.data)
			setTotal(res.total)
			setPages(res.pages)
		} catch {
			// ignore
		} finally {
			setIsLoading(false)
		}
	}, [page])

	useEffect(() => { fetchCampaigns() }, [fetchCampaigns])

	const handleDelete = async (e: React.MouseEvent, id: string) => {
		e.stopPropagation()
		try {
			await api.delete(`/outreach/campaigns/${id}`)
			fetchCampaigns()
		} catch {
			// ignore
		}
	}

	return (
		<>
			<div className="flex items-center justify-between gap-4 flex-wrap">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
					<p className="text-sm text-muted-foreground mt-1">{t("subtitle")}</p>
				</div>
				<Button onClick={onCreateNew} className="bg-[#2D8A5E] hover:bg-[#2d5c3f] text-white gap-2">
					<Plus className="h-4 w-4" />
					{t("newCampaign")}
				</Button>
			</div>

			{isLoading ? (
				<div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
			) : campaigns.length === 0 ? (
				<div className="text-center py-16">
					<Send className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
					<p className="text-muted-foreground">{t("empty")}</p>
					<p className="text-sm text-muted-foreground mt-1">{t("emptySubtitle")}</p>
				</div>
			) : (
				<div className="space-y-3">
					{campaigns.map((campaign) => {
						const statusConf = CAMPAIGN_STATUS_CONFIG[campaign.status] || CAMPAIGN_STATUS_CONFIG.draft
						return (
							<div
								key={campaign.id}
								onClick={() => onOpenCampaign(campaign)}
								className="flex items-center gap-4 p-4 rounded-lg border border-gray-100 hover:border-gray-200 cursor-pointer transition-colors"
							>
								<div className="h-10 w-10 rounded-lg bg-[#2D8A5E]/10 flex items-center justify-center shrink-0">
									<Send className="h-5 w-5 text-[#2D8A5E]" />
								</div>

								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2 flex-wrap">
										<p className="font-semibold text-gray-900 text-sm">{campaign.name}</p>
										<Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusConf.bg} ${statusConf.color}`}>
											{t(`status.${campaign.status}`)}
										</Badge>
										{campaign.source_type && <SourceBadge type={campaign.source_type} />}
									</div>
									<div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
										<span>{t("totalEmails", { count: campaign.total_emails })}</span>
										<span>{t("approvedEmails", { count: campaign.approved_emails })}</span>
										<span>{t("sentEmails", { count: campaign.sent_emails })}</span>
										<span>{formatDate(campaign.created_at)}</span>
									</div>
								</div>

								<div className="flex items-center gap-2 shrink-0">
									<Button
										variant="ghost"
										size="sm"
										onClick={(e) => handleDelete(e, campaign.id)}
										className="text-muted-foreground hover:text-red-500"
									>
										<Trash2 className="h-4 w-4" />
									</Button>
								</div>
							</div>
						)
					})}

					<Pagination page={page} pages={pages} total={total} limit={limit} onPageChange={setPage} />
				</div>
			)}
		</>
	)
}

// ── Create Campaign ──

function CreateCampaign({
	onBack,
	onCreated,
}: {
	onBack: () => void
	onCreated: (campaign: OutreachCampaign) => void
}) {
	const t = useTranslations("outreach")
	const [name, setName] = useState("")
	const [product, setProduct] = useState<OutreachProduct>("contractacio")
	const [deadlineDays, setDeadlineDays] = useState(7)
	const [minScore, setMinScore] = useState(60)
	const [candidates, setCandidates] = useState<OutreachCandidate[]>([])
	const [previewCount, setPreviewCount] = useState<number | null>(null)
	const [isLoadingPreview, setIsLoadingPreview] = useState(false)
	const [isCreating, setIsCreating] = useState(false)
	const [showCandidates, setShowCandidates] = useState(false)

	const fetchPreview = async () => {
		setIsLoadingPreview(true)
		try {
			const res = await api.get<PreviewResponse>(
				`/outreach/preview?deadlineWithinDays=${deadlineDays}&minRelevanceScore=${minScore}&emailType=verified_and_org&product=${product}`
			)
			setCandidates(res.data)
			setPreviewCount(res.total)
		} catch {
			setCandidates([])
			setPreviewCount(0)
		} finally {
			setIsLoadingPreview(false)
		}
	}

	useEffect(() => {
		setShowCandidates(false)
		const timer = setTimeout(fetchPreview, 500)
		return () => clearTimeout(timer)
	}, [deadlineDays, minScore, product])

	const handleCreate = async () => {
		if (!name.trim()) return
		setIsCreating(true)
		try {
			const res = await api.post<{ success: boolean; data: OutreachCampaign }>(
				"/outreach/campaigns",
				{ name: name.trim(), deadlineWithinDays: deadlineDays, minRelevanceScore: minScore, product }
			)
			onCreated(res.data)
		} catch {
			// ignore
		} finally {
			setIsCreating(false)
		}
	}

	return (
		<>
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="sm" onClick={onBack}>
					<ArrowLeft className="h-4 w-4" />
				</Button>
				<div>
					<h1 className="text-2xl font-bold text-gray-900">{t("create.title")}</h1>
					<p className="text-sm text-muted-foreground mt-1">{t("create.subtitle")}</p>
				</div>
			</div>

			<div className="space-y-6">
				<div className="space-y-2">
					<Label>{t("create.name")}</Label>
					<Input
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("create.namePlaceholder")}
					/>
				</div>

				<div className="space-y-2">
					<Label>Producte</Label>
					<div className="grid grid-cols-2 gap-2">
						<button
							type="button"
							onClick={() => setProduct("contractacio")}
							className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
								product === "contractacio"
									? "border-[#2D8A5E] bg-[#2D8A5E]/5 text-[#2D8A5E]"
									: "border-gray-200 text-gray-600 hover:border-gray-300"
							}`}
						>
							<SourceBadge type="tender" size="md" />
							OptimGov Contractació
						</button>
						<button
							type="button"
							onClick={() => setProduct("subvencions")}
							className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
								product === "subvencions"
									? "border-amber-500 bg-amber-50 text-amber-700"
									: "border-gray-200 text-gray-600 hover:border-gray-300"
							}`}
						>
							<SourceBadge type="grant" size="md" />
							OptimGov Subvencions
						</button>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label>{t("create.deadlineDays")}</Label>
						<Input
							type="number"
							min={1}
							max={90}
							value={deadlineDays}
							onChange={(e) => setDeadlineDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 7)))}
						/>
						<p className="text-xs text-muted-foreground">{t("create.deadlineDaysHelp")}</p>
					</div>
					<div className="space-y-2">
						<Label>{t("create.minScore")}</Label>
						<Input
							type="number"
							min={0}
							max={100}
							value={minScore}
							onChange={(e) => setMinScore(Math.max(0, Math.min(100, parseInt(e.target.value) || 60)))}
						/>
						<p className="text-xs text-muted-foreground">{t("create.minScoreHelp")}</p>
					</div>
				</div>

				{/* Preview */}
				<div className="rounded-lg border border-gray-200 bg-gray-50/50 overflow-hidden">
					<div
						className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100/50 transition-colors"
						onClick={() => previewCount && previewCount > 0 && setShowCandidates(!showCandidates)}
					>
						<div className="flex items-center gap-2">
							<Eye className="h-4 w-4 text-muted-foreground" />
							<span className="text-sm font-medium">{t("create.preview")}</span>
						</div>
						{isLoadingPreview ? (
							<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
						) : (
							<span className="text-sm font-semibold text-[#2D8A5E]">
								{previewCount !== null ? t("create.candidatesFound", { count: previewCount }) : "..."}
							</span>
						)}
					</div>

					{showCandidates && candidates.length > 0 && (
						<div className="border-t border-gray-200 max-h-72 overflow-y-auto">
							{candidates.map((c, i) => (
								<div
									key={`${c.contact_id}-${c.source_type}-${c.source_id}`}
									className={`flex items-center gap-3 px-4 py-3 text-sm ${i > 0 ? "border-t border-gray-100" : ""}`}
								>
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2 flex-wrap">
											<span className="font-medium text-gray-900">{c.contact_name}</span>
											<RecipientBadge type={c.recipient_type} />
											<span className="text-xs text-muted-foreground">{c.contact_email}</span>
										</div>
										{c.contact_organization && c.recipient_type === "contact" && (
											<div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
												<Building2 className="h-3 w-3" />
												{c.contact_organization}
											</div>
										)}
									</div>
									<div className="text-right shrink-0">
										<div className="flex items-center gap-1.5 justify-end">
											<SourceBadge type={c.source_type} />
											<p className="text-xs text-gray-700 truncate max-w-[200px]">{c.source_title}</p>
										</div>
										<div className="flex items-center gap-3 justify-end mt-0.5">
											{c.ai_relevance_score != null && (
												<span className="text-xs font-medium text-[#2D8A5E]">{c.ai_relevance_score}%</span>
											)}
											{c.source_deadline && (
												<span className="text-xs text-muted-foreground flex items-center gap-1">
													<Calendar className="h-3 w-3" />
													{formatDate(c.source_deadline)}
												</span>
											)}
											{c.source_budget != null && (
												<span className="text-xs text-muted-foreground">{formatCurrency(c.source_budget)}</span>
											)}
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				<Button
					onClick={handleCreate}
					disabled={!name.trim() || isCreating || previewCount === 0}
					className="bg-[#2D8A5E] hover:bg-[#2d5c3f] text-white gap-2 w-full"
				>
					{isCreating ? (
						<>
							<Loader2 className="h-4 w-4 animate-spin" />
							{t("create.generating")}
						</>
					) : (
						<>
							<Send className="h-4 w-4" />
							{t("create.generate")}
						</>
					)}
				</Button>
			</div>
		</>
	)
}

// ── Campaign Detail ──

function CampaignDetail({
	campaign,
	onBack,
	onUpdate,
}: {
	campaign: OutreachCampaign
	onBack: () => void
	onUpdate: (c: OutreachCampaign) => void
}) {
	const t = useTranslations("outreach")
	const [emails, setEmails] = useState<OutreachEmail[]>([])
	const [isLoading, setIsLoading] = useState(true)
	const [total, setTotal] = useState(0)
	const [page, setPage] = useState(1)
	const [pages, setPages] = useState(1)
	const [statusFilter, setStatusFilter] = useState<OutreachEmailStatus | "all">("all")
	const [expandedEmail, setExpandedEmail] = useState<string | null>(null)
	const [isApproving, setIsApproving] = useState(false)
	const limit = 30

	const fetchEmails = useCallback(async () => {
		setIsLoading(true)
		try {
			const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() })
			if (statusFilter !== "all") params.set("status", statusFilter)

			const res = await api.get<CampaignEmailsResponse>(
				`/outreach/campaigns/${campaign.id}/emails?${params}`
			)
			setEmails(res.data)
			setTotal(res.total)
			setPages(res.pages)
		} catch {
			// ignore
		} finally {
			setIsLoading(false)
		}
	}, [campaign.id, page, statusFilter])

	useEffect(() => { fetchEmails() }, [fetchEmails])

	const refreshCampaign = async () => {
		try {
			const res = await api.get<{ success: boolean; data: OutreachCampaign }>(
				`/outreach/campaigns/${campaign.id}`
			)
			onUpdate(res.data)
		} catch {
			// ignore
		}
	}

	const handleApproveAll = async () => {
		setIsApproving(true)
		try {
			await api.post(`/outreach/campaigns/${campaign.id}/approve-all`, {})
			await Promise.all([fetchEmails(), refreshCampaign()])
		} catch {
			// ignore
		} finally {
			setIsApproving(false)
		}
	}

	const handleUpdateStatus = async (emailId: string, status: "approved" | "rejected") => {
		try {
			await api.patch<{ success: boolean }>(`/outreach/emails/${emailId}/status`, { status })
			setEmails((prev) => prev.map((e) => e.id === emailId ? { ...e, status } : e))
			await refreshCampaign()
		} catch {
			// ignore
		}
	}

	const statusConf = CAMPAIGN_STATUS_CONFIG[campaign.status] || CAMPAIGN_STATUS_CONFIG.draft
	const statusFilters: Array<OutreachEmailStatus | "all"> = ["all", "pending", "approved", "rejected", "sent"]

	return (
		<>
			{/* Header */}
			<div className="flex items-center justify-between gap-4 flex-wrap">
				<div className="flex items-center gap-3">
					<Button variant="ghost" size="sm" onClick={onBack}>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<div>
						<div className="flex items-center gap-2">
							<h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
							<Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusConf.bg} ${statusConf.color}`}>
								{t(`status.${campaign.status}`)}
							</Badge>
						</div>
						<div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
							<span>{t("totalEmails", { count: campaign.total_emails })}</span>
							<span>{t("approvedEmails", { count: campaign.approved_emails })}</span>
							<span>{t("sentEmails", { count: campaign.sent_emails })}</span>
						</div>
					</div>
				</div>

				<div className="flex items-center gap-2">
					<Button
						onClick={handleApproveAll}
						disabled={isApproving}
						variant="outline"
						className="gap-2"
					>
						{isApproving ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<CheckCheck className="h-4 w-4" />
						)}
						{t("detail.approveAll")}
					</Button>
				</div>
			</div>

			{/* Filters */}
			<div className="flex items-center gap-2 flex-wrap">
				{statusFilters.map((sf) => (
					<Button
						key={sf}
						variant={statusFilter === sf ? "default" : "outline"}
						size="sm"
						onClick={() => { setStatusFilter(sf); setPage(1) }}
						className={statusFilter === sf ? "bg-[#2D8A5E] hover:bg-[#2d5c3f] text-white" : ""}
					>
						{sf === "all" ? t("detail.filterAll") : t(`emailStatus.${sf}`)}
						{sf === "all" && ` (${campaign.total_emails})`}
					</Button>
				))}
			</div>

			{/* Email list */}
			{isLoading ? (
				<div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
			) : emails.length === 0 ? (
				<div className="text-center py-16">
					<Mail className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
					<p className="text-muted-foreground">{t("detail.noEmails")}</p>
				</div>
			) : (
				<div className="space-y-3">
					{emails.map((email) => {
						const conf = STATUS_CONFIG[email.status]
						const StatusIcon = conf.icon
						const isExpanded = expandedEmail === email.id

						return (
							<div key={email.id} className="rounded-lg border border-gray-100 overflow-hidden">
								{/* Email header row */}
								<div
									className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
									onClick={() => setExpandedEmail(isExpanded ? null : email.id)}
								>
									<StatusIcon className={`h-4 w-4 shrink-0 ${conf.color}`} />

									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2 flex-wrap">
											<p className="font-medium text-sm text-gray-900 truncate">{email.contact_name}</p>
											<RecipientBadge type={email.recipient_type} />
											<span className="text-xs text-muted-foreground">{email.contact_email}</span>
											{email.contact_organization && email.recipient_type === "contact" && (
												<span className="text-xs text-muted-foreground flex items-center gap-1">
													<Building2 className="h-3 w-3" />
													{email.contact_organization}
												</span>
											)}
										</div>
										<p className="text-xs text-muted-foreground mt-0.5 truncate">
											{email.subject}
										</p>
									</div>

									<div className="flex items-center gap-3 shrink-0">
										<SourceBadge type={email.source_type} />
										{email.source_budget && (
											<span className="text-xs text-muted-foreground flex items-center gap-1">
												<Banknote className="h-3 w-3" />
												{formatCurrency(email.source_budget)}
											</span>
										)}
										{email.source_deadline && (
											<span className="text-xs text-muted-foreground flex items-center gap-1">
												<Calendar className="h-3 w-3" />
												{formatDate(email.source_deadline)}
											</span>
										)}
									</div>

									{/* Actions */}
									{(email.status === "pending" || email.status === "approved" || email.status === "rejected") && (
										<div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
											{email.status !== "approved" && (
												<Button
													variant="ghost"
													size="sm"
													onClick={() => handleUpdateStatus(email.id, "approved")}
													className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
												>
													<CheckCircle2 className="h-4 w-4" />
												</Button>
											)}
											{email.status !== "rejected" && (
												<Button
													variant="ghost"
													size="sm"
													onClick={() => handleUpdateStatus(email.id, "rejected")}
													className="text-red-500 hover:text-red-600 hover:bg-red-50"
												>
													<XCircle className="h-4 w-4" />
												</Button>
											)}
										</div>
									)}
								</div>

								{/* Expanded email body */}
								{isExpanded && (
									<div className="border-t border-gray-100 p-4 bg-gray-50/30 space-y-3">
										<div>
											<p className="text-xs font-medium text-muted-foreground mb-1">{t("detail.emailSubject")}</p>
											<p className="text-sm font-medium text-gray-900">{email.subject}</p>
										</div>
										<div>
											<p className="text-xs font-medium text-muted-foreground mb-1">{t("detail.emailBody")}</p>
											<div className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded-md border p-3">
												{email.body}
											</div>
										</div>
										<div>
											<div className="flex items-center gap-2 mb-1">
												<p className="text-xs font-medium text-muted-foreground">
													{email.source_type === "grant" ? "Subvenció associada" : t("detail.relatedTender")}
												</p>
												<SourceBadge type={email.source_type} />
											</div>
											<p className="text-sm text-gray-700">{email.source_title}</p>
										</div>
										{email.ai_relevance_reason && (
											<div>
												<p className="text-xs font-medium text-muted-foreground mb-1">{t("detail.relevanceReason")}</p>
												<p className="text-xs text-gray-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
													{email.ai_relevance_reason}
												</p>
											</div>
										)}
									</div>
								)}
							</div>
						)
					})}

					<Pagination page={page} pages={pages} total={total} limit={limit} onPageChange={setPage} />
				</div>
			)}
		</>
	)
}
